import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { DBLead, LeadOutcome, LeadRejectionReason } from "@/lib/db-types";

/** Det møtebookeren får se om ett lead (RPC claim_next_lead). brreg_raw og interne felt sendes ikke. */
export interface LeadPayload {
  lead: Omit<DBLead, "brreg_raw" | "created_by" | "preferred_user_id">;
  list_name: string;
  /** VIB-3032: ringemanus for listen. */
  list_script: string | null;
  contacts: { id: string; full_name: string; title: string | null; email: string | null; phone: string | null }[];
  attempts: { created_at: string; outcome: LeadOutcome; note: string | null; user_name: string }[];
  brreg: { founded_on: string | null; leader: string | null };
  /** VIB-3050: bookeren har allerede fått «Ikke svar» på leadet i køen sin. */
  second_try?: boolean;
  /** VIB-3050: siste «Ring tilbake»-tidspunkt. */
  call_back_at?: string | null;
}

/**
 * Mitt nåværende lead. RPC-en claim_next_lead gir tilbake leadet jeg allerede har
 * låst, ellers låser den neste i poolen og gir det til meg (ett om gangen,
 * usynlig pool). Derfor aldri refetch av seg selv: nytt lead hentes først når et
 * utfall er logget og `leads` invalideres. null = poolen er tom.
 */
export function useCurrentLead(options: { enabled?: boolean } = {}) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["current-lead", user?.id],
    queryFn: async (): Promise<LeadPayload | null> => {
      const { data, error } = await supabase.rpc("claim_next_lead", { p_list_id: null });
      if (error) throw new Error(error.message);
      return (data as LeadPayload | null) ?? null;
    },
    enabled: !!user?.id && (options.enabled ?? true),
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export interface LogLeadAttemptInput {
  leadId: string;
  outcome: Exclude<LeadOutcome, "meeting_booked">;
  note?: string | null;
  callBackAt?: string | null;
  /** VIB-3032: årsak og revurderingsdato (YYYY-MM-DD, null = aldri) for «Avvist». */
  reason?: LeadRejectionReason | null;
  revisitOn?: string | null;
}

/** Logger utfall på det låste leadet. Neste lead hentes automatisk når currentLead invalideres. */
export function useLogLeadAttempt() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: LogLeadAttemptInput) => {
      const { error } = await supabase.rpc("log_lead_attempt", {
        p_lead_id: input.leadId,
        p_outcome: input.outcome,
        p_note: input.note ?? null,
        p_call_back_at: input.callBackAt ?? null,
        p_reason: input.reason ?? null,
        p_revisit_on: input.revisitOn ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await invalidateFor(qc, "lead_attempts", { leadId: undefined });
      await invalidateFor(qc, "leads", { userId: user?.id });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

/** «Hopp over»: frigir låsen uten utfall (maks tre per dag, håndhevet i databasen). */
export function useReleaseLead() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (leadId: string) => {
      const { error } = await supabase.rpc("release_lead", { p_lead_id: leadId });
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await invalidateFor(qc, "leads", { userId: user?.id });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

/** Admin: «Slett person» på et lead (anonymisering). */
export function useAnonymizeLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (leadId: string) => {
      const { error } = await supabase.rpc("anonymize_lead", { p_lead_id: leadId });
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await invalidateFor(qc, "leads");
      await invalidateFor(qc, "customer_contacts");
      toast.success("Leadet er anonymisert");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

/**
 * Gjør et lead om til kunde (idempotent, RPC convert_lead_to_customer). Kunden
 * kobles IKKE til PowerOffice her; det skjer i create-project eller via
 * «Koble til PowerOffice» på kundekortet.
 */
export function useConvertLeadToCustomer() {
  const qc = useQueryClient();
  const { activeUser, isImpersonating } = useImpersonation();
  return useMutation({
    mutationFn: async (leadId: string): Promise<string> => {
      const { data, error } = await supabase.rpc("convert_lead_to_customer", {
        p_lead_id: leadId,
        p_on_behalf_of: isImpersonating && activeUser ? activeUser.id : null,
      });
      if (error) throw new Error(error.message);
      return data as string;
    },
    onSuccess: async (customerId) => {
      await invalidateFor(qc, "leads", { customerId });
      await invalidateFor(qc, "customers", { customerId });
      await invalidateFor(qc, "customer_contacts", { customerId });
      await invalidateFor(qc, "opportunities");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
